import { cn } from '@portals-platform/ui';
import { AlertCircle } from 'lucide-react';
import { useId } from 'react';
import { useFieldError } from '../lib/use-field-error.js';
import { useRegistration } from '../lib/use-registration.js';

interface CheckboxFieldProps {
  name: string;
  label: string;
  sublabel?: string;
  className?: string;
}

/**
 * Standalone checkbox with its own label — not wrapped by `FormField`, so it renders its
 * own error. Previously it rendered none at all: a failing `mustAcceptTerms` refinement
 * left the user with a form that would not submit and no visible reason.
 */
export function CheckboxField({ name, label, sublabel, className }: CheckboxFieldProps) {
  const uid = useId();
  const error = useFieldError(name);
  const bind = useRegistration<HTMLInputElement>(name);

  const id = `${name}-${uid}`;
  const errorId = `${id}-error`;
  const subId = `${id}-sub`;
  const describedBy = [error && errorId, sublabel && !error && subId].filter(Boolean).join(' ');

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="flex cursor-pointer items-start gap-2.5 py-1 text-[13px]">
        <input
          id={id}
          type="checkbox"
          aria-invalid={!!error}
          aria-describedby={describedBy || undefined}
          className={cn(
            'mt-0.5 size-4 shrink-0 accent-primary',
            error && 'outline outline-1 outline-offset-2 outline-destructive',
          )}
          {...bind}
        />
        <span className="leading-snug">
          {label}
          {sublabel && (
            <span id={subId} className="mt-0.5 block text-[11.5px] text-muted-foreground">
              {sublabel}
            </span>
          )}
        </span>
      </label>
      {error && (
        <p
          id={errorId}
          role="alert"
          className="flex items-center gap-1.5 pl-[26px] text-[11px] font-medium text-destructive"
        >
          <AlertCircle className="size-3 shrink-0" aria-hidden="true" />
          {error.message}
        </p>
      )}
    </div>
  );
}
